import {
  ConsoleLogger,
  DefaultDeviceController,
  LogLevel,
} from 'amazon-chime-sdk-js';
import { useCallback, useEffect, useRef, useState } from 'react';
import { pickDefaultDevice, toDeviceOptions, type MediaDeviceOption } from './chimeDevices';

export type { MediaDeviceOption };

/** 'pending' until the first permission attempt resolves. */
export type MediaAccessState = 'pending' | 'granted' | 'blocked' | 'unsupported';

/**
 * Prompt once for both devices, then fall back to separate prompts so a missing or
 * blocked microphone does not also cost us camera access (and vice versa).
 */
async function requestMediaPermission(): Promise<{ granted: boolean; release: () => void }> {
  const media = navigator.mediaDevices;
  const releaseAll = (streams: MediaStream[]) => (): void =>
    streams.forEach((stream) => stream.getTracks().forEach((t) => t.stop()));
  try {
    return { granted: true, release: releaseAll([await media.getUserMedia({ audio: true, video: true })]) };
  } catch {
    const results = await Promise.allSettled([
      media.getUserMedia({ audio: true }),
      media.getUserMedia({ video: true }),
    ]);
    const streams = results.flatMap((result) => (result.status === 'fulfilled' ? [result.value] : []));
    return { granted: streams.length > 0, release: releaseAll(streams) };
  }
}

/**
 * `enabled: false` keeps every camera and microphone untouched (no permission prompt), so a page can
 * mount the hook before it knows the visitor belongs on the call screen.
 */
export function useDevicePreview({ enabled = true }: { enabled?: boolean } = {}): {
  videoRef: (el: HTMLVideoElement | null) => void;
  audioDevices: MediaDeviceOption[];
  videoDevices: MediaDeviceOption[];
  audioDeviceId: string | undefined;
  videoDeviceId: string | undefined;
  setAudioDeviceId: (id: string) => void;
  setVideoDeviceId: (id: string) => void;
  access: MediaAccessState;
  requestAccess: () => void;
  error: string | null;
  stopPreview: () => Promise<void>;
  restartPreview: () => void;
  /** Chosen before joining; the call starts muted or with the camera off to match. */
  micOn: boolean;
  cameraOn: boolean;
  setMicOn: (on: boolean) => void;
  setCameraOn: (on: boolean) => void;
} {
  const controllerRef = useRef<DefaultDeviceController | null>(null);
  const videoElRef = useRef<HTMLVideoElement | null>(null);
  const [videoEl, setVideoEl] = useState<HTMLVideoElement | null>(null);
  const [audioDevices, setAudioDevices] = useState<MediaDeviceOption[]>([]);
  const [videoDevices, setVideoDevices] = useState<MediaDeviceOption[]>([]);
  const [audioDeviceId, setAudioDeviceId] = useState<string | undefined>();
  const [videoDeviceId, setVideoDeviceId] = useState<string | undefined>();
  const [access, setAccess] = useState<MediaAccessState>('pending');
  const [error, setError] = useState<string | null>(null);
  const [previewEpoch, setPreviewEpoch] = useState(0);
  const [micOn, setMicOn] = useState(true);
  const [cameraOn, setCameraOn] = useState(true);

  const ensureController = useCallback((): DefaultDeviceController => {
    if (!controllerRef.current) {
      const logger = new ConsoleLogger('UnifyChimePreview', LogLevel.WARN);
      controllerRef.current = new DefaultDeviceController(logger);
    }
    return controllerRef.current;
  }, []);

  const videoRef = useCallback((el: HTMLVideoElement | null) => {
    videoElRef.current = el;
    setVideoEl(el);
  }, []);

  /**
   * Await before the meeting's own device controller opens the same camera.
   * `destroy` closes the preview AudioContext; otherwise Chrome keeps the tab's
   * speaker indicator lit after the call, on whatever page the tab navigates to.
   */
  const stopPreview = useCallback(async () => {
    const controller = controllerRef.current;
    if (!controller) return;
    controllerRef.current = null;
    if (videoElRef.current) {
      controller.stopVideoPreviewForVideoInput(videoElRef.current);
    }
    try {
      await controller.destroy();
    } catch {
      /* already destroyed */
    }
  }, []);

  /** Reopens the camera after `stopPreview` when the join it was stopped for did not happen. */
  const restartPreview = useCallback(() => setPreviewEpoch((n) => n + 1), []);

  /** `forceUpdate` bypasses the SDK's device cache so hot-plugged devices appear. */
  const refreshDevices = useCallback(async () => {
    const controller = ensureController();
    const [audio, video] = await Promise.all([
      controller.listAudioInputDevices(true),
      controller.listVideoInputDevices(true),
    ]);
    setAudioDevices(toDeviceOptions(audio, 'Microphone'));
    setVideoDevices(toDeviceOptions(video, 'Camera'));
    setAudioDeviceId((prev) => pickDefaultDevice(audio, prev));
    setVideoDeviceId((prev) => pickDefaultDevice(video, prev));
  }, [ensureController]);

  const requestAccess = useCallback(() => {
    void (async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        setAccess('unsupported');
        setError('Camera and microphone require a secure (https) connection.');
        return;
      }
      const { granted, release } = await requestMediaPermission();
      let listError: string | null = null;
      try {
        // Listed while the probe streams are open: some browsers (Safari) hide device ids once capture stops.
        await refreshDevices();
      } catch (e) {
        listError = e instanceof Error ? e.message : 'Could not list devices';
      } finally {
        release();
      }
      // Granted only once the devices are listed, so the no-microphone notice never flashes before the default is picked.
      setAccess(granted ? 'granted' : 'blocked');
      setError(
        listError ??
          (granted
            ? null
            : 'Camera and microphone access is blocked. Allow it in your browser settings, then try again.'),
      );
    })();
  }, [refreshDevices]);

  useEffect(() => {
    if (!enabled) return undefined;
    requestAccess();
    const reload = (): void => {
      void refreshDevices().catch(() => {
        /* transient enumeration failure; the next devicechange retries */
      });
    };
    navigator.mediaDevices?.addEventListener('devicechange', reload);
    return () => navigator.mediaDevices?.removeEventListener('devicechange', reload);
  }, [enabled, refreshDevices, requestAccess]);

  useEffect(() => {
    if (!cameraOn) {
      // Release the camera (its light goes off) while the preview is turned off.
      void controllerRef.current?.stopVideoInput().catch(() => undefined);
      return undefined;
    }
    if (!videoEl || !videoDeviceId) return undefined;
    const controller = ensureController();
    let cancelled = false;
    void (async () => {
      try {
        await controller.startVideoInput(videoDeviceId);
        if (cancelled) return;
        controller.startVideoPreviewForVideoInput(videoEl);
        setError(null);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Could not start camera preview');
        }
      }
    })();
    return () => {
      cancelled = true;
      controller.stopVideoPreviewForVideoInput(videoEl);
    };
  }, [ensureController, videoEl, videoDeviceId, previewEpoch, cameraOn]);

  useEffect(() => () => void controllerRef.current?.destroy(), []);

  return {
    videoRef,
    audioDevices,
    videoDevices,
    audioDeviceId,
    videoDeviceId,
    setAudioDeviceId,
    setVideoDeviceId,
    access,
    requestAccess,
    error,
    stopPreview,
    restartPreview,
    micOn,
    cameraOn,
    setMicOn,
    setCameraOn,
  };
}
